import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from 'react-dropzone';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as FieldTypes from '../Shared/FieldTypes/FieldTypes.js'
import AddItemCustomFields from "../Shared/AddItemCustomFields.js";


let debounceTimer = null;

// Custom AddItemPopup for the cross-submission Q&A list (DataTable.js). Combines two
// existing patterns rather than inventing a new one: the Title/Description/PDF fields are
// copied from the per-submission page's own AddQAItemPopup (Submissions/SubmissionQA.js),
// and the submission picker below is the same inline search-and-pick shape
// Submissions/AddItemPopup.js already uses for its own Certificate field, pointed at the
// main Submissions search endpoint instead.
//
// fixedSubmissionId: when this popup is reused inside one submission's own Q&A table
// (SubmissionQADataTable.js), the submission is already known, so the picker is skipped
// entirely and this id is used directly.
const SubmissionQAListAddItemPopup = ({ globalContext, fixedSubmissionId = null }) => {

    const AppContext = globalContext;

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [pendingFile, setPendingFile] = useState(null);
    const [errorTitle, setErrorTitle] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [submissionSearch, setSubmissionSearch] = useState("");
    const [submissionResults, setSubmissionResults] = useState([]);
    const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
    const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
    const [selectedSubmissionTitle, setSelectedSubmissionTitle] = useState("");
    const [errorSubmission, setErrorSubmission] = useState(false);
    const [countries, setCountries] = useState([]);

    // Same generic countries endpoint + fetch-once-then-match-by-id pattern DataTable.js's own
    // ShowRegistrationsFilterCountryName already uses — search results only carry CountryId
    // (Submission.cs's own [ResultColumn], already selected by SeachSubmissions), not a flag
    // URL, so it's resolved client-side rather than widening that shared, heavily-used query.
    useEffect(() => {
        fetch('/umbraco/api/RIMSProducts/Countries')
            .then((response) => response.ok ? response.json() : [])
            .then((data) => setCountries(data || []))
            .catch((error) => console.log("error: " + error));
    }, []);

    // Same stand-in AppContext pattern AddQAItemPopup/QAItemDetail use: there is no real QA
    // item yet, so the rich-text field is backed by local draft state instead.
    const draftFieldContext = {
        currentItem: { Description: description },
        setCurrentItem: (updated) => setDescription(updated.Description)
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) setPendingFile(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': [] },
        multiple: false
    });

    const doSubmissionSearchTask = (textSearch) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => loadSubmissionResults(textSearch), 500);
    };

    const loadSubmissionResults = (search) => {
        setIsLoadingSubmissions(true);

        // sortBy must be a non-empty SubmissionSortBy member name or this endpoint 400s.
        const url = `/umbraco/api/Submission/Submissions?search=${encodeURIComponent(search)}&page=1&sortBy=Title&sortType=ASCENDING`;

        fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => setSubmissionResults(result || []))
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsLoadingSubmissions(false));
    };

    const selectSubmission = (submission) => {
        setSelectedSubmissionId(submission.Id);
        setSelectedSubmissionTitle(submission.Title);
        setErrorSubmission(false);
    };

    const createItem = () => {
        const submissionId = fixedSubmissionId ?? selectedSubmissionId;

        let isValid = true;

        if (!title || title.trim() == "") {
            setErrorTitle(true);
            isValid = false;
        } else setErrorTitle(false);

        if (!fixedSubmissionId) {
            if (!selectedSubmissionId) {
                setErrorSubmission(true);
                isValid = false;
            } else setErrorSubmission(false);
        }

        if (!isValid) return;

        setIsSaving(true);

        fetch("/umbraco/api/SubmissionQA/QA", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                SubmissionId: submissionId,
                Title: title,
                Description: description,
                CustomFieldsAnswers: AppContext.newItemCustomFieldsAnswers,
            })
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));

                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((createdItem) => {
                if (!createdItem || !createdItem.Id) throw new Error("Invalid response");

                if (!pendingFile) return createdItem;

                const formData = new FormData();
                formData.append('files', pendingFile);

                return fetch(`/umbraco/api/SubmissionQA/QAFiles?submissionId=${submissionId}&qaId=${createdItem.Id}`, {
                    method: 'POST',
                    body: formData,
                })
                    .catch((error) => console.log("error uploading file: " + error))
                    .then(() => createdItem);
            })
            .then(() => {
                // The created item's own response doesn't carry the joined
                // Country/Certificate/Submission display columns, so re-fetch page 1
                // rather than splicing an incomplete row into the table.
                AppContext.loadData({ nextPage: false });
                AppContext.updateListTotalCount();
                AppContext.showAddItemPopup(false);
            })
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsSaving(false));
    };

    return (
        <div style={{ position: "fixed", backgroundColor: "rgba(1, 1, 1, 0.5)", height: "100%", width: "100%", top: 0, left: 0, zIndex: 1000, overflowY: "auto" }}>
            <div className="add-task-popup">

                <div style={{ display: "flex", flexDirection: "row" }}>
                    <div style={{ width: "50%" }}>
                        <h3>Add Q&amp;A item</h3>
                    </div>
                    <div style={{ width: "50%", textAlign: "right" }}>
                        <button className="close-button" onClick={() => AppContext.showAddItemPopup(false)}><i className="fas fa-times"></i></button>
                    </div>
                </div>

                <input
                    className={`form-field-text ${errorTitle ? "form-field-error" : ""}`}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{ width: "100%" }}
                    type="text"
                    placeholder="Title *"
                />
                {errorTitle && (
                    <span className="form-field-error-message">This field is necessary</span>
                )}

                <br /><br />

                {!fixedSubmissionId && (
                    <>
                        <label style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "6px" }}>Submission</label>

                        <input
                            className={`form-field-text ${errorSubmission ? "form-field-error" : ""}`}
                            type="text"
                            placeholder="🔍 Search for the Submission *"
                            style={{ width: "100%" }}
                            value={submissionSearch}
                            onChange={(e) => { setSubmissionSearch(e.target.value); doSubmissionSearchTask(e.target.value); }}
                        />
                        {errorSubmission && (
                            <span className="form-field-error-message">Choose a submission</span>
                        )}

                        {submissionSearch.trim() != "" && (
                            <div style={{ maxHeight: "160px", overflow: "auto", marginTop: "8px", marginBottom: "10px" }}>
                                {isLoadingSubmissions ? (
                                    <div style={{ textAlign: "center" }}><LoadingSpinnerImage /></div>
                                ) : submissionResults.length == 0 ? (
                                    <div className="no-more-results">No submissions found</div>
                                ) : (
                                    submissionResults.map((row) => {
                                        const countryFlag = countries.find((c) => c.CountryId === row.CountryId)?.FlagLink;

                                        return (
                                        <div key={row.Id} className="datatable-row" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                            <div style={{ flex: 1 }}>
                                                <div>{row.Title}</div>
                                                {(row.CertificateName || row.Country) && (
                                                    <div style={{ fontSize: "11px", color: "#a0aec0", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginLeft: "8px" }}>
                                                        {row.CertificateName && (
                                                            <span>Certificate: {row.CertificateName}</span>
                                                        )}
                                                        {row.CertificateName && row.Country ? <span> • </span> : null}
                                                        {row.Country && (
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                                                {countryFlag && <img src={countryFlag} alt={`${row.Country} flag`} style={{ height: "12px" }} />}
                                                                {row.Country}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <span
                                                style={{ borderRadius: "10px", backgroundColor: selectedSubmissionId === row.Id ? "#c0c0c0" : "#b4dcff", padding: "1px 9px", cursor: "pointer" }}
                                                onClick={() => selectSubmission(row)}
                                            >
                                                {selectedSubmissionId === row.Id ? "Added" : "Add"}
                                            </span>
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {selectedSubmissionId ? (
                            <p>Submission selected: <b>{selectedSubmissionTitle}</b></p>
                        ) : (
                            <p><span>Submission selected:</span> None.</p>
                        )}

                        <br />
                    </>
                )}

                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "6px" }}>Description</label>
                <FieldTypes.ItemDetailsField_Text AppContext={draftFieldContext} field={"Description"} fieldTitle={"Description"} mandatory={false} richText={true} forceIsEditing={true} displayFieldOnly={true} allowImages={false} />

                <br />

                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "6px" }}>Document</label>

                {pendingFile ? (
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                        <i className="fas fa-file-pdf g-mr-8" style={{ color: "#e53e3e" }}></i>
                        <div style={{ flex: 1 }}>{pendingFile.name}</div>
                        <button className="action-button options-button" onClick={() => setPendingFile(null)} title="Remove"><i className="fas fa-times"></i></button>
                    </div>
                ) : (
                    <div
                        {...getRootProps()}
                        style={{
                            border: `2px dashed ${isDragActive ? "#28a745" : "#ccc"}`,
                            borderRadius: "8px",
                            padding: "16px",
                            textAlign: "center",
                            marginTop: "10px",
                            background: isDragActive ? "#f6fff9" : "#fff",
                            cursor: "pointer"
                        }}
                    >
                        <input {...getInputProps()} />
                        <span><i className="fas fa-cloud-upload-alt g-mr-5"></i> Drag &amp; drop a PDF here, or click to select</span>
                    </div>
                )}

                {AppContext.enableCustomFields ?
                    <>
                        <br />
                        <AddItemCustomFields entityType={AppContext.entityTypeId} context={AppContext} />
                    </>
                    : null}
                <br />

                <div className="action-button-bar">
                    <div className="action-button-bar-left">
                        <button className="action-button cancel-button" onClick={() => AppContext.showAddItemPopup(false)}>Close</button>
                    </div>
                    <div className="action-button-bar-center"></div>
                    <div className="action-button-bar-right">
                        {isSaving ? <LoadingSpinnerImage /> : (
                            <button className="action-button save-button" onClick={createItem}>Add Q&amp;A item</button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SubmissionQAListAddItemPopup;
